import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Flex, Row, Space, Tag, Typography } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { useGetCountries } from 'api/countries/use-get-countries.ts';
import type { IGetCargoRes } from 'api/swagger';
import { usePaginationParams } from 'shared/hooks/use-pagination-params.ts';
import { useRoleGuard, UserRole } from 'shared/hooks/use-role-guard.ts';
import { useSelectCargoStore } from 'shared/stores/use-select-cargos.ts';
import { getCargoTypeLabel } from 'shared/utils/cargos/cargo-type-labels.ts';
import { findCountriesById } from 'shared/utils/countries';
import { isFullyCompleted, isFullyLoaded } from 'shared/utils/statuses.ts';
import type { AddressDirection } from 'types/address.ts';
import type { IPaginationInfo } from 'types/api-query.ts';
import { OrderStatus } from 'types/order.ts';
import { CargoAddressCol } from 'components/cargos/cargo-address-col';
import { CargoInfoCol } from 'components/cargos/cargo-info-col';
import { CargoItemStatuses } from 'components/cargos/cargo-item-status';
import { CargoCheckbox } from 'components/cargos/cargos-list/components/cargo-checkbox';
import { ColorCircles } from 'components/common/color-circles';
import { ImportantCargoDocumentFlags } from 'components/documents/important-cargo-document-flags';
import { Pagination } from 'components/pagination';
import styles from './cargos-map-list.module.scss';

interface ICargosMapListProps {
  cargos: IGetCargoRes[];
  activeCargoId: number | null;
  pagination?: IPaginationInfo | null;
  direction: AddressDirection;
}

const { Text } = Typography;

export const CargosMapList: React.FC<ICargosMapListProps> = (props) => {
  const { cargos, activeCargoId, pagination, direction } = props;
  const { t } = useTranslation();
  const { handlePageChange } = usePaginationParams();
  const { data: countries } = useGetCountries();
  const { selectedCargos } = useSelectCargoStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const allowSelectItems = useRoleGuard([UserRole.admin, UserRole.assistant, UserRole.transport_manager]);

  const isUnloading = direction === 'unloading';

  useEffect(() => {
    if (activeCargoId) {
      const myElement = document.getElementById(String(activeCargoId));
      const topPos = myElement?.offsetTop;
      if (containerRef?.current && topPos) containerRef.current.scrollTop = topPos - 65;
    }
  }, [activeCargoId]);

  return (
    <div ref={containerRef} className={styles.container}>
      {cargos.map((cargo) => {
        const loadingCountry = findCountriesById(cargo.loadingAddress.countryId, countries);
        const unloadingCountry = findCountriesById(cargo.unloadingAddress.countryId, countries);

        const isSelected = selectedCargos.has(cargo.id);

        const cardClasses = classNames(styles.card, {
          [styles.activeCard]: activeCargoId === cargo.id,
          [styles.selectedCard]: isSelected,
          [styles.selectedCard]: isSelected,
          [styles.canceled]: cargo.order.status === OrderStatus.cancelled,
        });

        return (
          <div key={cargo.id} className={cardClasses} id={String(cargo.id)}>
            <div className={styles.idsBox}>
              <div>
                {allowSelectItems ? <CargoCheckbox cargo={cargo} className={styles.checkbox} /> : null}
                <Space align='center'>
                  {cargo.client?.isVip && (
                    <Tag color='success' className={styles.vipTag}>
                      {t('client:type_vip')}
                    </Tag>
                  )}
                  <Link to={`/cargos/${cargo.id}`}>{cargo.compositeId}</Link>
                  <Tag>{getCargoTypeLabel(cargo.type)}</Tag>
                  <Row align='middle'>
                    <ColorCircles cargoId={cargo.id} grayCircleClassnames={styles.grayDot} />
                  </Row>
                </Space>
              </div>
              <Flex gap='small' justify='flex-end'>
                <Link className={styles.orderId} to={`/orders/${cargo.orderId}`}>
                  {cargo.order.compositeId}
                </Link>
                <Text className={styles.secondary}>
                  {loadingCountry?.code} <ArrowRightOutlined className={styles.arrow} /> {unloadingCountry?.code}
                </Text>
              </Flex>
            </div>
            <div className={styles.clientIdBox}>
              <Text className={styles.secondary}>{cargo.order.clientOrderNumber}</Text>
            </div>

            <div className={styles.body}>
              <CargoInfoCol cargo={cargo} />
              <div className={styles.address}>
                <CargoAddressCol
                  isOverdueVisible={isUnloading ? isFullyCompleted(cargo.statuses) : isFullyLoaded(cargo.statuses)}
                  needCalculateDate={
                    (isUnloading ? !isFullyCompleted(cargo.statuses) : !isFullyLoaded(cargo.statuses)) &&
                    cargo.order.status !== OrderStatus.cancelled
                  }
                  address={isUnloading ? cargo.unloadingAddress : cargo.loadingAddress}
                  customAddress={isUnloading ? cargo.unloadingCustomAddress : cargo.loadingCustomAddress}
                  dateFrom={isUnloading ? cargo.unloadingDateFrom : cargo.loadingDateFrom}
                  dateTo={isUnloading ? cargo.unloadingDateTo : cargo.loadingDateTo}
                  actualDate={isUnloading ? cargo.deliveryDate : cargo.pickupDate}
                />
              </div>
            </div>
            <div>
              <CargoItemStatuses statuses={cargo.statuses} assetIsReturned={cargo.assetIsReturned} />
              {cargo.documents && (
                <div className={styles.documents}>
                  <ImportantCargoDocumentFlags documents={cargo.documents} />
                </div>
              )}
            </div>
          </div>
        );
      })}
      {pagination ? (
        <Pagination
          total={pagination?.total}
          page={pagination?.currentPage}
          limit={pagination?.limit}
          onChange={handlePageChange}
        />
      ) : null}
    </div>
  );
};
